import React from "react";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import useAnnouncementForm from '../hooks/useAnnouncementForm';
import ImageSelector from "../../elements/ImageSelector/ui/ImageSelector";
import OpenModalButton from "../../elements/OpenModalButton/ui/OpenModalButton";
import TagInput from "../../elements/TagInput/TagInput";
import DropdownUnit from "../../../common/components/DropdownUnit";
import { units } from "../../adform/constants/units";
import Loading from "../../../common/components/Loading";
import ErrorModal from '../../modals/error_modal/ui/Main';
import warningIcon from '../../../assets/icons/WarningCircle.svg';

/* ── Reusable field label ── */
function Label({ children, required }) {
    return (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {children}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
    );
}

/* ── Input / Textarea ── */
function Field({ label, required, error, children }) {
    return (
        <div>
            {label && <Label required={required}>{label}</Label>}
            {error && (
                <div className="flex items-center gap-1 mb-1">
                    <img src={warningIcon} alt="" className="w-4 h-4" />
                    <span className="text-xs text-red-500">{error}</span>
                </div>
            )}
            {children}
        </div>
    );
}

const inputCls = (err) =>
    `w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-colors bg-white
    ${err ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-[#147F26]'}`;

/* ── Section card ── */
function Card({ title, children }) {
    return (
        <div className="bg-white rounded-2xl p-5 flex flex-col gap-4">
            {title && <h3 className="text-sm font-semibold text-gray-800">{title}</h3>}
            {children}
        </div>
    );
}

function EditForm({ announcementId, announcementData }) {
    const navigate = useNavigate();

    const {
        priceType, setPriceType,
        announcementName, handleAnnouncementInputChange,
        videoLink, handleVideoLinkChange,
        description, handleDescriptionChange,
        handleSelectCategory,
        price, setPrice,
        handleImagesChange,
        isSubmitted,
        userData,
        tagsArray,
        customTags, handleCustomTagsChange, handleAddTag, handleRemoveTag,
        additionalPhones, handleAddPhone, handlePhoneChange, handleDeletePhone,
        isLoading,
        showErrorModal, setShowErrorModal,
        errorMessage,
        handlePublish,
        handlePreview,
        phoneErrors,
        volumeUnit,
        handleVolumeChange,
    } = useAnnouncementForm(announcementData);

    const onPublish = async () => {
        const result = await handlePublish(announcementId);
        if (result?.success) navigate(`/announcement/${result.id}`);
    };

    const onPreview = () => {
        const previewData = handlePreview();
        if (previewData) navigate('/preview', { state: { previewData } });
    };

    const nameError = isSubmitted && !announcementName ? t('enter_announcement_name') : null;
    const descError = isSubmitted && !description ? t('enter_description') : null;
    const priceError = isSubmitted && priceType === 'fixed' && !price ? t('announcementPriceError') : null;

    return (
        <div className="flex flex-col lg:flex-row gap-5 items-start">

            {/* ── Left column ── */}
            <div className="flex-1 min-w-0 flex flex-col gap-4">

                {/* Dealer badge */}
                {announcementData?.is_dealer && (
                    <div className="flex items-center gap-2.5 bg-[#147F26]/8 border border-[#147F26]/20 rounded-2xl px-4 py-3">
                        <svg className="w-5 h-5 text-[#147F26] flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.05 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.287-3.957z" />
                        </svg>
                        <span className="text-sm font-semibold text-[#147F26]">{t('dealerBadge')}</span>
                    </div>
                )}

                {/* Photos */}
                <Card title={t('announcementPhoto')}>
                    <ImageSelector
                        onImagesChange={handleImagesChange}
                        initialImages={announcementData?.image_urls || []}
                    />
                </Card>

                {/* Basic info */}
                <Card title={t('announcement')}>
                    <Field label={t('announcement_title')} required error={nameError}>
                        <input
                            type="text"
                            className={inputCls(nameError)}
                            placeholder={t('enter_announcement_name')}
                            value={announcementName}
                            onChange={e => handleAnnouncementInputChange(e.target.value)}
                        />
                    </Field>

                    <Field label={t('select_category')}>
                        <OpenModalButton
                            type="category"
                            onSelect={handleSelectCategory}
                            selectedCategoryId={announcementData?.category_id}
                            selectedSubcategoryId={announcementData?.subcategory_id}
                        />
                    </Field>

                    <Field label={t('description')} required error={descError}>
                        <textarea
                            className={inputCls(descError)}
                            placeholder={t('enter_description')}
                            value={description}
                            rows={5}
                            onChange={e => handleDescriptionChange(e.target.value)}
                        />
                    </Field>
                </Card>

                {/* Price */}
                <Card title={t('price')}>
                    {/* Type toggle */}
                    <div className="flex gap-2">
                        {['fixed', 'negotiable'].map(type => (
                            <button
                                key={type}
                                onClick={() => setPriceType(type)}
                                className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors border
                                    ${priceType === type
                                        ? 'bg-[#147F26] text-white border-[#147F26]'
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                            >
                                {type === 'fixed' ? t('fixed_price') : t('negotiable')}
                            </button>
                        ))}
                    </div>

                    {priceType === 'fixed' && (
                        <Field error={priceError}>
                            <div className="flex gap-3">
                                <input
                                    type="number"
                                    className={`${inputCls(priceError)} flex-1`}
                                    placeholder={t('enter_price')}
                                    value={price && price !== 'None' ? price : ''}
                                    onChange={e => setPrice(e.target.value)}
                                />
                                <div className="w-44 flex-shrink-0">
                                    <DropdownUnit
                                        text={t('volume_production')}
                                        options={units}
                                        selected={volumeUnit}
                                        onOptionSelect={handleVolumeChange}
                                    />
                                </div>
                            </div>
                        </Field>
                    )}
                </Card>

                {/* Tags */}
                <Card title={t('keywords')}>
                    <TagInput
                        value={customTags}
                        onChange={handleCustomTagsChange}
                        onAddTag={handleAddTag}
                        onRemoveTag={handleRemoveTag}
                        tags={tagsArray}
                        isSubmitted={isSubmitted}
                    />
                </Card>

                {/* Video */}
                <Card title={t('video_link')}>
                    <input
                        type="url"
                        className={inputCls(false)}
                        placeholder="https://youtube.com/..."
                        value={videoLink}
                        onChange={e => handleVideoLinkChange(e.target.value)}
                    />
                </Card>
            </div>

            {/* ── Right column ── */}
            <div className="w-full lg:w-[300px] lg:flex-shrink-0 flex flex-col gap-4 lg:sticky lg:top-[90px]">

                {/* Contact info */}
                <Card title={t('your_data')}>
                    <Field label={t('contact_name')}>
                        <input
                            type="text"
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-100 bg-gray-50 text-sm text-gray-500 outline-none cursor-not-allowed"
                            value={userData?.name || ''}
                            disabled
                        />
                    </Field>

                    <Field label={t('phone')}>
                        <input
                            type="tel"
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-100 bg-gray-50 text-sm text-gray-500 outline-none cursor-not-allowed"
                            value={userData?.phone_number || ''}
                            disabled
                        />
                    </Field>

                    {/* Additional phones */}
                    {additionalPhones.map((phone, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                            <input
                                type="tel"
                                className={inputCls(phoneErrors[idx])}
                                placeholder={t('enter_additional_phone')}
                                value={phone}
                                onChange={e => handlePhoneChange(idx, e.target.value)}
                            />
                            <button
                                onClick={() => handleDeletePhone(idx)}
                                className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-xl hover:bg-red-50 transition-colors"
                            >
                                <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    ))}

                    <button
                        onClick={handleAddPhone}
                        className="flex items-center gap-2 text-sm text-[#147F26] hover:text-[#0f6b1f] transition-colors py-1"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
                        </svg>
                        {t('add_more')}
                    </button>
                </Card>

                {/* Action buttons */}
                <div className="flex flex-col gap-3">
                    <button
                        onClick={onPublish}
                        disabled={isLoading}
                        className="w-full py-3 rounded-xl bg-[#147F26] hover:bg-[#0f6b1f] text-white text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                        {isLoading ? <Loading /> : (
                            <>
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {t('publish')}
                            </>
                        )}
                    </button>
                    <button
                        onClick={onPreview}
                        disabled={isLoading}
                        className="w-full py-3 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors disabled:opacity-60"
                    >
                        {t('preview')}
                    </button>
                </div>
            </div>

            {showErrorModal && (
                <ErrorModal
                    showLogoutModal={showErrorModal}
                    setShowLogoutModal={setShowErrorModal}
                    message={errorMessage}
                />
            )}
        </div>
    );
}

export default EditForm;
